import type {UserData} from '@Libraries/user.library';
import {serializeUrl} from '@Utils/url.util';

export const MY_STUFF_HASH_INDEX = 1;
export const MY_STUFF_HASH_PARAM_INDEX = 2;
export const MY_STUFF_NON_REACT_CONTENT_LOADED_EVENT = 'nonReactMSContentLoaded';

/**
 * Object storing all the hashes of the pages supported in myStuff.
 * Lives here (import-safe TS, no transitive globals) so pure libraries and unit tests can use it; legacy my-stuff-library.js re-exports it.
 */
export const MY_STUFF_HASHES = {
  HASH_INDEX: 1,
  myDesigns: '#/designs',
  sharedWithMe: '#/shared',
  favorites: '#/favorites',
  rejectedTemplates: '#/rejected-templates',
  search: '#/search',
  team: '#/team-space',
  brands: '#/brands',
  socialPosts: '#/posts',
  sharedSocialPosts: '#/shared-posts',
  socialAccounts: '#/social-accounts',
  contentPlanner: '#/content-planner',
  emailCampaigns: '#/email-marketing-campaigns',
  emailCampaignsShared: '#/shared-email-marketing-campaigns',
  emailCampaignPerformance: '#/email-marketing-campaign-performance',
  emailCampaignAudience: '#/email-marketing-campaign-audience',
  emailCampaignMailingLists: '#/email-marketing-campaign-mailing-lists',
  emailCampaignSignupForms: '#/email-marketing-campaign-signup-forms',
  emailCampaignSettings: '#/email-marketing-campaign-settings',
  trash: '#/trash',
  trashSocialPosts: '#/trash-posts',
  trashEmailCampaigns: '#/trash-email-marketing-campaigns',
  designerInsights: '#/insights',
  designerActivity: '#/activity',
  designerEarnings: '#/earnings',
  designTips: '#/designtips',
  classProjects: '#/class-projects',
  keepClassroomsFree: '#/keep-free',
  myClients: '#/my-clients',
  recommendedForYou: '#/recommended',
  improveTemplates: '#/improve-templates',
  businessProfile: '#/business-profile',
  events: '#/my-events',
  eventsSharedWithMe: '#/events-shared',
  eventsCalendar: '#/events-calendar',
  eventsStripe: '#/events-stripe',
  promoteEvent: '#/promote-event',
  manageEvent: '#/manage-event',
  eventTickets: '#/event-tickets',
  mailingLists: '#/mailing-lists',
  signupForms: '#/signup-forms',
  folders: '#/folders',
  sharedFolders: '#/shared-folders',
  trashFolders: '#/trash-folders',
  teamSpaceFolders: '#/team-space-folders',
};

export type UserDataFromTeamsPayload = UserData & {
  email: string;
  profilePictureURLSmall: string;
};

export type TeamMembers = Record<string, UserDataFromTeamsPayload>;

export interface Team {
  idTeam: string;
  name: string;
  seats: number;
  type: TeamType;
  members: TeamMembers;
}

export enum TeamType {
  REGULAR,
  RESELLER,
  RESELLER_CLIENT,
}

export const isPageHashIncludedInList = (pageHash: string, pageHashes: string[]): boolean => {
  return pageHashes.length > 0 && pageHashes.includes(pageHash);
};

export const isPageRelatedToMyDesigns = (pageHash: string): boolean => {
  const myDesignsPagesList = [
    MY_STUFF_HASHES.myDesigns,
    MY_STUFF_HASHES.sharedWithMe,
    MY_STUFF_HASHES.search,
    MY_STUFF_HASHES.favorites,
    MY_STUFF_HASHES.recommendedForYou,
    MY_STUFF_HASHES.folders,
    MY_STUFF_HASHES.sharedFolders,
  ];

  if (!isDesignerActionCenterEnabled()) {
    myDesignsPagesList.push(MY_STUFF_HASHES.rejectedTemplates);
  }

  return isPageHashIncludedInList(pageHash, myDesignsPagesList);
};

export const isPageRelatedToFolders = (pageHash: string): boolean => {
  return isPageHashIncludedInList(pageHash, [MY_STUFF_HASHES.folders, MY_STUFF_HASHES.sharedFolders, MY_STUFF_HASHES.trashFolders, MY_STUFF_HASHES.teamSpaceFolders]);
};

export const isPageRelatedToMyEmails = (pageHash: string): boolean => {
  return isPageHashIncludedInList(pageHash, [
    MY_STUFF_HASHES.emailCampaigns,
    MY_STUFF_HASHES.emailCampaignSettings,
    MY_STUFF_HASHES.emailCampaignsShared,
    MY_STUFF_HASHES.emailCampaignPerformance,
    MY_STUFF_HASHES.emailCampaignAudience,
    MY_STUFF_HASHES.emailCampaignMailingLists,
    MY_STUFF_HASHES.emailCampaignSignupForms,
  ]);
};

export const isPageRelatedToEmailCampaigns = (pageHash: string): boolean => {
  return isPageHashIncludedInList(pageHash, [MY_STUFF_HASHES.emailCampaigns]);
};

export const isPageRelatedToEmailCampaignSettings = (pageHash: string): boolean => {
  return isPageHashIncludedInList(pageHash, [MY_STUFF_HASHES.emailCampaignSettings]);
};

export const isPageRelatedToEmailCampaignShared = (pageHash: string): boolean => {
  return isPageHashIncludedInList(pageHash, [MY_STUFF_HASHES.emailCampaignsShared]);
};


export const isPageRelatedToEmailCampaignPerformance = (pageHash: string): boolean => {
  return isPageHashIncludedInList(pageHash, [MY_STUFF_HASHES.emailCampaignPerformance]);
};

export const isPageRelatedToSocialMediaAccounts = (pageHash: string): boolean => {
  return isPageHashIncludedInList(pageHash, [MY_STUFF_HASHES.socialAccounts]);
};


export const SOCIAL_MEDIA_PUBLISHING_PAGE_HASHES: string[] = [MY_STUFF_HASHES.socialPosts, MY_STUFF_HASHES.socialAccounts, MY_STUFF_HASHES.sharedSocialPosts];

export const isPageRelatedToSocialMediaPublishing = (pageHash: string): boolean => {
  return isPageHashIncludedInList(pageHash, SOCIAL_MEDIA_PUBLISHING_PAGE_HASHES);
};

export const isContentPlannerPage = (pageHash: string): boolean => {
  return pageHash === MY_STUFF_HASHES.contentPlanner;
};

export const isClassProjectsPage = (pageHash: string): boolean => {
  return pageHash === MY_STUFF_HASHES.classProjects;
};

export const isTrashPage = (pageHash: string): boolean => {
  return isPageHashIncludedInList(pageHash, [MY_STUFF_HASHES.trash, MY_STUFF_HASHES.trashSocialPosts, MY_STUFF_HASHES.trashEmailCampaigns]);
};

export const isBrandKitsPage = (pageHash: string): boolean => {
  return pageHash === MY_STUFF_HASHES.brands;
};

export const isSocialMediaPostsPage = (pageHash: string = getMyStuffPageHash()): boolean => {
  return pageHash === MY_STUFF_HASHES.socialPosts;
};

export const isSharedSocialMediaPostsPage = (pageHash: string = getMyStuffPageHash()): boolean => {
  return pageHash === MY_STUFF_HASHES.sharedSocialPosts;
};

export const isSocialMediaAccountsPage = (pageHash: string = getMyStuffPageHash()): boolean => {
  return pageHash === MY_STUFF_HASHES.socialAccounts;
};

export const isRecommendationsPage = (pageHash: string = getMyStuffPageHash()): boolean => {
  return pageHash === MY_STUFF_HASHES.recommendedForYou;
};

export const shouldRecommendationsPageBeShown = (): boolean => {
  return !!window.reactPageProps.doesUserHaveRecommendationsAvailable;
};

export const isDesignerActionCenterEnabled = (): boolean => {
  return !!window.reactPageProps.isDesignerActionCenterEnabled;
};

export const getRejectedTemplatesCount = (): number => {
  const count = Number(window.reactPageProps.rejectedTemplatesCount);
  return Number.isFinite(count) && count > 0 ? count : 0;
};

export const isPagePartOfActionCenter = (pageHash: string = getMyStuffPageHash()): boolean => {
  return isPageHashIncludedInList(pageHash, [MY_STUFF_HASHES.improveTemplates, MY_STUFF_HASHES.rejectedTemplates]);
};

export const isImproveTemplatesPage = (pageHash: string = getMyStuffPageHash()): boolean => {
  return pageHash === MY_STUFF_HASHES.improveTemplates;
};

export const isEmailMarketingCampaignsPage = (pageHash: string = getMyStuffPageHash()): boolean => {
  return pageHash === MY_STUFF_HASHES.emailCampaigns;
};

export const isBusinessProfilesPage = (pageHash: string): boolean => {
  return pageHash === MY_STUFF_HASHES.businessProfile;
};

export const isPageRelatedToBusinessProfiles = (pageHash: string): boolean => {
  return isPageHashIncludedInList(pageHash, [MY_STUFF_HASHES.businessProfile, MY_STUFF_HASHES.brands]);
};

export const isPageRelatedToEventsFeature = (pageHash: string): boolean => {
  return isPageRelatedToEvents(pageHash) || isPageRelatedToEventPromotion(pageHash);
};

export const isPageRelatedToEvents = (pageHash: string): boolean => {
  return isPageHashIncludedInList(pageHash, [MY_STUFF_HASHES.events, MY_STUFF_HASHES.eventsCalendar, MY_STUFF_HASHES.eventsSharedWithMe, MY_STUFF_HASHES.eventsStripe]);
};

export const isPageRelatedToEventPromotion = (pageHash: string = getMyStuffPageHash()): boolean => {
  return isPageHashIncludedInList(pageHash, [MY_STUFF_HASHES.promoteEvent, MY_STUFF_HASHES.manageEvent, MY_STUFF_HASHES.eventTickets]);
};

export const isEventsPage = (pageHash: string = getMyStuffPageHash()): boolean => {
  return pageHash === MY_STUFF_HASHES.events;
};

export const isEventsSharedPage = (pageHash: string = getMyStuffPageHash()): boolean => {
  return pageHash === MY_STUFF_HASHES.eventsSharedWithMe;
};

export const isEventsHomePage = (pageHash: string = getMyStuffPageHash()): boolean => {
  return pageHash === MY_STUFF_HASHES.eventsCalendar;
};

export const isEventPromotionPage = (pageHash: string = getMyStuffPageHash()): boolean => {
  return pageHash === MY_STUFF_HASHES.promoteEvent;
};

export const isManageEventPage = (pageHash: string = getMyStuffPageHash()): boolean => {
  return pageHash === MY_STUFF_HASHES.manageEvent;
};

export const isEventTicketsPage = (pageHash: string = getMyStuffPageHash()): boolean => {
  return pageHash === MY_STUFF_HASHES.eventTickets;
};

export const isPageRelatedToContacts = (pageHash: string = getMyStuffPageHash()): boolean => {
  return pageHash === MY_STUFF_HASHES.signupForms || pageHash === MY_STUFF_HASHES.mailingLists;
};

export const shouldSharedEventsNotificationBeShown = (): boolean => {
  return !!window.reactPageProps.shouldShowSharedEventsNotification;
};

/**
 * Extracts and returns the page hash from the passed window location hash, such
 * that it can be compared with one of the constants from MY_STUFF_HASHES.
 * @return {string}
 */
export const getMyStuffPageHash = (): string => {
  return getPageHashFromMyStuffHash(window.location.hash);
};

export const getPageHashFromMyStuffHash = (hash: string): string => {
  return `#/${hash.split('/')[MY_STUFF_HASH_INDEX]}`;
};

let myStuffHashBeforeCurrent = '';
let isTrackingMyStuffHashHistory = false;

/**
 * The My Stuff hash the user was on before the current one; empty on the first page this document has shown.
 */
export const getMyStuffHashBeforeCurrent = (): string => {
  return myStuffHashBeforeCurrent;
};

export const startTrackingMyStuffHashHistory = (): void => {
  if (isTrackingMyStuffHashHistory) {
    return;
  }

  isTrackingMyStuffHashHistory = true;
  let currentHash = window.location.hash;

  window.addEventListener('hashchange', (): void => {
    myStuffHashBeforeCurrent = currentHash;
    currentHash = window.location.hash;
  });
};

export const getHashedIDFromMyStuffURL = (): string => {
  const afterPageHashPart  = window.location.hash.split('/')[MY_STUFF_HASH_PARAM_INDEX] ?? '';
  return afterPageHashPart.split('?')[0];
};

export const goToMyStuff = (): void => {
  window.location.href = window.PMW.util.site_url('posters/mine');
};

export const goToMyStuffHash = (pageHash: string): void => {
  window.location.href = window.PMW.util.site_url(`posters/mine${pageHash}`);
};

export const isTeamAdmin = (): boolean => {
  return !!window.reactPageProps?.isTeamAdmin;
};

export const isExPremiumUser = (): boolean => {
  return !!window.reactPageProps?.isExPremiumUser;
};

/**
 * Whether the viewer's request geolocates to the US. Bootstrapped only by `Views/Posters/MyStuff.php`
 * (`Services::geolocate()->isUserInUS()`); absent on every other page, so false there.
 */
export const isUserInUS = (): boolean => {
  return !!window.reactPageProps?.isUserInUS;
};

/**
 * Label of the events section in My Stuff navigation (sidebar item and breadcrumbs). US viewers can sell
 * tickets, everyone else gets event pages with RSVP. `my-stuff.js` carries a one-line legacy twin.
 */
export const getEventsSectionLabel = (): string => {
  return window.i18next.t(isUserInUS() ? 'pmwjs_events_and_tickets' : 'pmwjs_event_pages');
};

export const isMoreOptionsItemSelectedInCompactMyStuffNav = (selectedItemIndexFromPrimaryList: number, navItemsLimit: number): boolean => {
  return selectedItemIndexFromPrimaryList === -1 || selectedItemIndexFromPrimaryList >= navItemsLimit;
};
// codeReviewShahrukhLater: move to teams-library? with better typing and a more generic payload ig
const isTeamFromReactPagePropsValid = (): boolean => {
  const teamPageProps = window.reactPageProps?.team ?? null;

  if (!teamPageProps) {
    return false;
  }

  return (
    typeof teamPageProps.idTeam === 'string' &&
    typeof teamPageProps.name === 'string' &&
    typeof teamPageProps.seats === 'number' &&
    typeof teamPageProps.type === 'number' &&
    typeof teamPageProps.members === 'object'
  );
};
export const getTeamFromReactPageProps = (): Team | null => {
  if (!isTeamFromReactPagePropsValid()) {
    return null;
  }

  return (window.reactPageProps?.team as Team) ?? null;
};

export const getTeamNameFromPageProps = (): string => {
  return getTeamFromReactPageProps()?.name ?? window.i18next.t('pmwjs_team');
};

export const getUserTeamIDFromPageProps = (): string => {
  return getTeamFromReactPageProps()?.idTeam ?? '';
};

export const isBottomBarHidden = (): boolean => {
  const bottomBar = document.querySelector('.js-mystuff-bottom-bar');

  if (!bottomBar) {
    return true;
  }

  return window.getComputedStyle(bottomBar).display === 'none';
};

export const hideMyStuffBottomBar = (): void => {
  document.querySelector('.js-mystuff-bottom-bar')?.classList?.add('_hidden');
};

export const showMyStuffBottomBar = (): void => {
  document.querySelector('.js-mystuff-bottom-bar')?.classList?.remove('_hidden');
};

export const loadChristmasDiscountCodeBannerInDOMIfNeeded = (): void => {
  if (typeof window.PMW.mystuff.loadChristmasBannerInDOM === 'function') {
    window.PMW.mystuff.loadChristmasBannerInDOM();
  }
};

export const loadAIRedesignBannerInDOMIfNeeded = (): void => {
  if (typeof window.PMW.mystuff.loadAIRedesignBannerInDOM === 'function') {
    window.PMW.mystuff.loadAIRedesignBannerInDOM();
  }
};
export const MY_STUFF_SEARCH_TERM_QUERY_KEY = 'st';

export const getMyStuffSearchTermFromUrl = (): string | null => {
  const searchTerm = new URL(window.location.href).searchParams.get(MY_STUFF_SEARCH_TERM_QUERY_KEY);
  return searchTerm === null || searchTerm === '' ? null : searchTerm;
};

export const removeMyStuffSearchTermFromUrl = (): void => {
  const url = new URL(window.location.href);
  url.searchParams.delete(MY_STUFF_SEARCH_TERM_QUERY_KEY);
  history.replaceState(history.state, '', serializeUrl(url));
};

export const MY_STUFF_POSTER_SECTION_QUERY_KEY = 'ps';

/**
 * Rewrites the My Stuff URL in place and fires the hashchange a bare replaceState never does.
 */
export const replaceMyStuffUrlAndNotify = (url: URL): void => {
  const oldURL = window.location.href;
  history.replaceState(history.state, '', serializeUrl(url));
  window.dispatchEvent(new HashChangeEvent('hashchange', {oldURL, newURL: window.location.href}));
};
